<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon
} from 'front-library'
import {
  getCalculatePhases,
  CalculatePhase,
  deleteCalculatePhase
} from '@/app/core/services/calculate-phases'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const loading = ref(false)

/* Datos */
const search = ref()
const tableCalculatePhase = ref(null)
const calculatePhaseForDelete = ref()

/* Datos componente table */
const columns = ref([
  {
    name: 'phase_number',
    title: t('calculationPhases.list.phase'),
    table: 'phase_number'
  },
  {
    name: 'typeName',
    title: t('calculationPhases.list.outputType'),
    table: 'target_types.name'
  },
  {
    name: 'conceptTarget',
    title: t('calculationPhases.list.entryConcept'),
    table: 'concept_target.name'
  },
  {
    name: 'conceptIterative',
    title: t('calculationPhases.list.exitConcept'),
    table: 'concept_iterative.name'
  },
  {
    name: 'listConcepts',
    title: t('calculationPhases.list.activationConcepts'),
    table: 'concepts_name'
  },
  {
    name: 'updated_at',
    title: t('calculationPhases.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<CalculatePhase[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createCalculatePhase = () => {
  void router.push({ name: 'CreateCalculatePhase' })
}
const updateCalculatePhase = (id: any) => {
  void router.push({ name: 'UpdateCalculatePhase', params: { id } })
}
const getCalculatePhase = (id: any) => {
  void router.push({ name: 'GetCalculatePhase', params: { id } })
}

/* Eliminar fase de cálculo */
const removeCalculatePhase = (values: any) => {
  showModal.value = true
  calculatePhaseForDelete.value = values.id
}
const deleteConfirm = async () => {
  loading.value = true
  showModal.value = false
  await deleteCalculatePhase(calculatePhaseForDelete.value)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('calculationPhases.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void calculatePhases()
    })
    .catch(async (error: any) => {
      notificationError(error, 'calculationPhases', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar fases de cálculo */
const calculatePhases = async () => {
  loading.value = true
  try {
    const data: any = await getCalculatePhases(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'calculationPhases', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="t('calculationPhases.list.actionStatus')"
    :description="t('modal.description')"
    :acceptQuestion="
      t('modal.acceptQuestion', {
        action: t('table.action.delete')
      })
    "
    @closeModal="closeModal"
    @otherMethod="deleteConfirm()" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('calculationPhases.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('calculationPhases.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="calculatePhases" />
          <DBtn
            @click.prevent="calculatePhases"
            v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createCalculatePhase()"
          v-if="store.hasPermission('phase:create')">
          {{
            t('form.button.create', {
              form: t('calculationPhases.list.title', 0)
            })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableCalculatePhase"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), calculatePhases()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), calculatePhases()">
      <template #listConcepts="data: any">
        <div class="flex items-center">
          <div
            v-for="(concept, index) in data.concepts_name
              .split(',')
              .slice(0, 1)"
            :key="index">
            {{ concept }}
          </div>
          <div
            v-if="data.concepts_name.split(',').length > 1"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            {{ data.concepts_name.split(',').length - 1 }}+
          </div>
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('phase:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getCalculatePhase(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('phase:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateCalculatePhase(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('phase:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeCalculatePhase(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
